import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";

export default function ChangePasswordPage() {
  const { user, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ current: "", next: "", again: "" });
  const [busy, setBusy] = useState(false);
  const forced = !!user?.must_change_password;
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.next !== f.again) { toast.error("Yeni şifreler birbiriyle aynı değil"); return; }
    if (f.next.length < 8 || !/[0-9]/.test(f.next) || !/[a-zA-ZçğıöşüÇĞİÖŞÜ]/.test(f.next)) {
      toast.error("Şifre en az 8 karakter olmalı ve harf ile rakam içermeli"); return;
    }
    setBusy(true);
    try {
      await changePassword(f.current, f.next);
      toast.success("Şifreniz değiştirildi");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.toString?.() || "Şifre değiştirilemedi");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 border border-slate-200 shadow-sm" data-testid="change-password-card">
        <div className="h-11 w-11 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center mb-4"><KeyRound className="h-5 w-5" /></div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Şifrenizi Değiştirin</h1>
        <p className="text-sm text-slate-500 mt-1" data-testid="change-password-intro">
          {forced ? "Güvenliğiniz için devam etmeden önce kendinize yeni bir şifre belirlemelisiniz." : "Mevcut şifrenizi girip yeni şifrenizi belirleyin."}
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div><Label>Mevcut şifre</Label>
            <Input type="password" value={f.current} onChange={set("current")} autoComplete="current-password" required data-testid="change-password-current" /></div>
          <div><Label>Yeni şifre</Label>
            <Input type="password" value={f.next} onChange={set("next")} autoComplete="new-password" required data-testid="change-password-new" />
            <p className="text-xs text-slate-500 mt-1">En az 8 karakter; harf ve rakam içermeli.</p></div>
          <div><Label>Yeni şifre (tekrar)</Label>
            <Input type="password" value={f.again} onChange={set("again")} autoComplete="new-password" required data-testid="change-password-again" /></div>
          <Button type="submit" disabled={busy} className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="change-password-submit">
            {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Şifreyi Değiştir
          </Button>
        </form>
        <div className="flex justify-between mt-4 text-sm">
          {!forced && <button type="button" onClick={() => navigate(-1)} className="text-slate-600 hover:text-slate-900" data-testid="change-password-back">Geri dön</button>}
          <button type="button" onClick={logout} className="text-slate-500 hover:text-slate-800 ml-auto" data-testid="change-password-logout">Çıkış yap</button>
        </div>
      </Card>
    </div>
  );
}
